import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowUpDown, Boxes, CheckCircle2, PackageX, RefreshCw, Search, Truck, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton, Tip } from "@/components/ui";
import { api, canManage, type StockItem } from "@/lib/api";
import { BestBeforePanel } from "./BestBefore";
import { ReachPanel } from "./Reach";
import { SlowMoversPanel } from "./SlowMovers";
import { ModuleGate } from "@/components/ModuleGate";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { StockSyncButton, StockSyncInfo } from "@/components/StockSync";
import { msg, t, tn } from "@/lib/i18n";

/** Ab hier gilt ein Artikel als knapp (später je Kunde/Artikel einstellbar) */
const LOW = 10;
const PAGE_SIZE = 100;

type Filter = "all" | "instock" | "low" | "out" | "incoming";
type SortKey = "sku" | "available" | "onHand";

export function StockPage() {
  const { tenantId } = useTenant();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "available", dir: 1 });
  const filter = (params.get("filter") as Filter | null) ?? "all";
  const [page, setPage] = useState(1);
  // neue Suche/Filter/Sortierung → zurück auf Seite 1
  useEffect(() => setPage(1), [term, filter, sort.key, sort.dir]);
  // Suche, Filter, Sortierung und Seiten auf dem Server – funktioniert auch bei 20.000+ Artikeln
  const q = useQuery({
    queryKey: ["stock", tenantId, "page", term, filter, sort.key, sort.dir, page],
    queryFn: () => api.stock.page(tenantId, { ...(term ? { search: term } : {}), filter, sort: sort.key, dir: sort.dir === 1 ? "asc" : "desc", page, pageSize: PAGE_SIZE, low: LOW }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  const items = q.data?.items ?? [];
  const sum = q.data?.summary;
  const counts = { all: sum?.all ?? 0, instock: sum?.instock ?? 0, out: sum?.out ?? 0, low: sum?.low ?? 0, incoming: sum?.incoming ?? 0 };
  const units = sum?.units ?? 0;
  const outOfSync = items.filter((s) => s.inSyncWithShop === false).length;
  const lastUpdate = sum?.lastUpdate ?? null;
  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const v = params.get("view");
  const view = v === "mhd" || v === "reach" || v === "slow" ? v : "stock";
  const tabs = (
    <div className="max-w-full overflow-x-auto">
    <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" role="tablist">
      {([["stock", msg("Bestand")], ["reach", msg("Reichweite & Nachbestellen")], ["slow", msg("Ladenhüter")], ["mhd", msg("MHD & Chargen")]] as const).map(([k, label]) => (
        <button key={k} role="tab" aria-selected={view === k} onClick={() => setParams(k === "stock" ? {} : { view: k }, { replace: true })} className={cx("h-8 px-4 rounded-md text-[13px] font-medium whitespace-nowrap", view === k ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
          {t(label)}
        </button>
      ))}
    </div>
    </div>
  );

  if (view === "reach") {
    return (
      <div className="pb-16">
        <PageHeader title={t("Bestände")} description={t("Wie lange reicht der Bestand beim aktuellen Absatz – und was muss wann nachbestellt werden?")} />
        <div className="px-4 md:px-8 space-y-4">
          {tabs}
          <ReachPanel />
        </div>
      </div>
    );
  }

  if (view === "slow") {
    return (
      <div className="pb-16">
        <PageHeader title={t("Bestände")} description={t("Was liegt zu lange? Gebundenes Kapital, Lagerkosten und was ihr tun könnt.")} />
        <div className="px-4 md:px-8 space-y-4">
          {tabs}
          <ModuleGate module="analytics" feature="slow_movers" title={t("Ladenhüter-Analyse")}><SlowMoversPanel /></ModuleGate>
        </div>
      </div>
    );
  }

  if (view === "mhd") {
    return (
      <div className="pb-16">
        <PageHeader title={t("Bestände")} description={t("Mindesthaltbarkeit je Charge – Warnungen nach euren Schwellen, stündlich vom Lager aktualisiert.")} />
        <div className="px-4 md:px-8 space-y-4">
          {tabs}
          <BestBeforePanel />
        </div>
      </div>
    );
  }

  const setFilter = (f: Filter) => {
    setParams(f === "all" ? {} : { filter: f }, { replace: true });
    // mit Bestand: größter Bestand zuerst; ausverkauft/knapp: kleinster zuerst
    if (f === "instock") setSort({ key: "available", dir: -1 });
    else if (f === "out" || f === "low") setSort({ key: "available", dir: 1 });
  };
  const toggleSort = (key: SortKey) => setSort((s) => ({ key, dir: s.key === key ? ((-s.dir) as 1 | -1) : 1 }));

  return (
    <div className="pb-16">
      <PageHeader
        title={t("Bestände")}
        description={
          <span className="inline-flex items-center gap-1.5">
            <RefreshCw className="size-3.5" />
            {lastUpdate ? t("Vom Lager aktualisiert {lastUpdate} · alle 10 Minuten automatisch", { lastUpdate: fmtRelative(lastUpdate) }) : t("Wird alle 10 Minuten vom Lager abgeglichen")}
          </span>
        }
        actions={<StockSyncButton />}
      />
      <div className="px-4 md:px-8 space-y-4">
        {tabs}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={<Boxes className="size-4" />} label={t("Einheiten im Lager")} value={q.isLoading ? null : fmtNumber(units)} sub={tn(counts.instock, "{n} Artikel mit Bestand", "{n} Artikel mit Bestand")} active={filter === "instock"} onClick={() => setFilter(filter === "instock" ? "all" : "instock")} />
          <Kpi icon={<PackageX className="size-4" />} label={t("Ausverkauft")} value={q.isLoading ? null : fmtNumber(counts.out)} sub={counts.out ? t("im Shop nicht bestellbar") : t("alles bestellbar")} tone={counts.out ? "danger" : "success"} active={filter === "out"} onClick={() => setFilter(filter === "out" ? "all" : "out")} />
          <Kpi icon={<AlertTriangle className="size-4" />} label={t("Knapp (≤ {LOW})", { LOW })} value={q.isLoading ? null : fmtNumber(counts.low)} sub={counts.low ? t("Nachschub einplanen") : t("kein Artikel knapp")} tone={counts.low ? "warning" : "success"} active={filter === "low"} onClick={() => setFilter(filter === "low" ? "all" : "low")} />
          <Kpi icon={<Truck className="size-4" />} label={t("Im Zulauf")} value={q.isLoading ? null : fmtNumber(counts.incoming)} sub={t("per Avis angekündigt")} active={filter === "incoming"} onClick={() => setFilter(filter === "incoming" ? "all" : "incoming")} />
        </div>

        <StockSyncInfo />

        {outOfSync > 0 && (
          <div className="flex items-center gap-2 rounded-xl border border-warning/25 bg-warning-soft/60 px-4 py-2.5 text-[13px]">
            <AlertTriangle className="size-4 text-warning" />
            {outOfSync} {outOfSync === 1 ? t("Artikel weicht") : t("Artikel weichen")} {t("auf dieser Seite vom Shop-Bestand ab – der nächste Abgleich korrigiert das automatisch.")}
          </div>
        )}

        {/* Suche groß über die volle Breite, Filter darunter (wie Aufträge, Artikel, Retouren, Wareneingänge) */}
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Bestand suchen: SKU oder Name …")} aria-label={t("Bestände durchsuchen")} leading={<Search className="size-4" />} suffix={search ? <button onClick={() => setSearch("")} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Suche leeren")}><X className="size-3.5" /></button> : undefined} className="h-11 text-[14px]" />
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex flex-wrap p-0.5 rounded-lg bg-surface-2 border border-border">
            {([["all", msg("Alle")], ["instock", msg("Mit Bestand")], ["out", msg("Ausverkauft")], ["low", msg("Knapp")], ["incoming", msg("Im Zulauf")]] as const).map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium flex items-center gap-1.5", filter === k ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
                {t(label)} <span className="text-fg-subtle tabular-nums">{counts[k]}</span>
              </button>
            ))}
          </div>
        </div>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="text-[12px] text-fg-subtle border-b border-border bg-surface-2/50">
                <tr>
                  <SortTh label={t("Artikel")} active={sort.key === "sku"} onClick={() => toggleSort("sku")} className="pl-4" />
                  <th className="h-9 px-3 font-medium text-right"><Tip content={t("An die Shops gemeldet: Lager-verfügbar (+ Zulauf, falls aktiviert) − im Portal reserviert − Puffer")}><span>{t("Im Shop")}</span></Tip></th>
                  <SortTh label={t("Verfügbar")} active={sort.key === "available"} onClick={() => toggleSort("available")} className="text-right" />
                  <SortTh label={t("Physisch")} active={sort.key === "onHand"} onClick={() => toggleSort("onHand")} className="text-right" />
                  <th className="h-9 px-3 font-medium text-right"><Tip content={t("Im Lager für übertragene Aufträge reserviert")}><span>{t("Reserviert")}</span></Tip></th>
                  <th className="h-9 px-3 font-medium text-right"><Tip content={t("Aufträge, die noch nicht im Lager sind (eingegangen, zurückgehalten, Stornofenster) – binden schon Bestand")}><span>{t("Im Portal reserviert")}</span></Tip></th>
                  <th className="h-9 px-3 font-medium text-right">{t("Gesperrt")}</th>
                  <th className="h-9 px-3 font-medium text-right">{t("Im Zulauf")}</th>
                  <th className="h-9 px-3 font-medium text-right">{t("Puffer")}</th>
                  <th className="h-9 pl-3 pr-4 font-medium">{t("Shop")}</th>
                </tr>
              </thead>
              <tbody>
                {q.isLoading && Array.from({ length: 8 }, (_, i) => <tr key={i} className="border-b border-border"><td colSpan={10} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td></tr>)}
                {items.map((s) => <StockRow key={s.productId} s={s} />)}
              </tbody>
            </table>
          </div>
          {!q.isLoading && items.length === 0 && (
            <EmptyState icon={<Boxes className="size-5" />} title={counts.all ? t("Keine Artikel für diesen Filter") : t("Noch keine Bestände")}>
              {counts.all ? (filter === "out" ? t("Nichts ausverkauft.") : t("Passe Filter oder Suche an.")) : t("Sobald Ware im Lager eingebucht ist, erscheinen die Bestände hier.")}
            </EmptyState>
          )}
          {total > PAGE_SIZE && (
            <div className="flex items-center gap-3 px-4 py-2.5 border-t border-border text-[12.5px] text-fg-muted">
              <span className="tabular-nums">{fmtNumber((page - 1) * PAGE_SIZE + 1)}–{fmtNumber(Math.min(total, page * PAGE_SIZE))} {t("von")} {fmtNumber(total)}</span>
              <div className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label={t("Vorherige Seite")}>{t("‹ Zurück")}</Button>
                <span className="tabular-nums px-1">{page} / {fmtNumber(pages)}</span>
                <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} aria-label={t("Nächste Seite")}>{t("Weiter ›")}</Button>
              </div>
            </div>
          )}
        </Card>
        <p className="text-[12px] text-fg-subtle">{t("Verfügbar = physisch − reserviert − gesperrt (Lager). Im Shop = verfügbar (+ Zulauf, falls aktiviert) − im Portal reserviert − Puffer; dieser Wert wird an deine Shops übertragen.")}</p>
      </div>
    </div>
  );
}

/** Pufferbestand je Artikel (leer = Kunden-Standard) – Klick zum Ändern */
function BufferCell({ s }: { s: StockItem }) {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState("");
  if (!canManage(role)) return <span className="tabular-nums text-fg-muted">{s.buffer ? fmtNumber(s.buffer) : "—"}</span>;
  if (!edit) return <button onClick={() => { setV(""); setEdit(true); }} className="tabular-nums text-fg-muted hover:text-fg hover:underline" title={t("Puffer für diesen Artikel ändern")}>{s.buffer ? fmtNumber(s.buffer) : "—"}</button>;
  const save = async () => {
    setEdit(false);
    const n = v.trim() === "" ? null : Math.max(0, Math.round(Number(v)));
    await api.products.patch(tenantId, s.productId, { stockBuffer: n });
    toast.success(n == null ? `${s.sku}: Kunden-Standard für Puffer` : `${s.sku}: Puffer ${n} Stück`);
    void qc.invalidateQueries({ queryKey: ["stock", tenantId] });
  };
  return <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onBlur={() => void save()} onKeyDown={(e) => e.key === "Enter" && void save()} placeholder={t("Std.")} inputMode="numeric" className="w-14 h-7 rounded-md border border-border bg-surface px-1.5 text-right text-[13px] tabular-nums" />;
}


function StockRow({ s }: { s: StockItem }) {
  const shown = s.sellable ?? s.available;
  const tone = shown === 0 ? "danger" : shown <= LOW ? "warning" : undefined;
  return (
    <tr className="border-b border-border last:border-0 hover:bg-surface-2/50">
      <td className="pl-4 pr-3 py-2.5">
        <Link to={`/products?open=${s.productId}`} className="font-medium hover:underline">{s.name}</Link>
        <div className="font-mono text-[11.5px] text-fg-muted">{s.sku}</div>
      </td>
      <td className="px-3 py-2.5 text-right">
        <span className={cx("inline-flex items-center gap-1.5 font-semibold tabular-nums text-[14px]", tone === "danger" && "text-danger", tone === "warning" && "text-warning")}>
          {s.sellable != null ? fmtNumber(s.sellable) : "—"}
        </span>
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">{fmtNumber(s.available)}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-fg-muted">{fmtNumber(s.onHand)}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-fg-muted">{s.reserved ? fmtNumber(s.reserved) : "—"}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{s.portalReserved ? <Link to="/orders?status=on_hold" className="text-warning hover:underline">{fmtNumber(s.portalReserved)}</Link> : <span className="text-fg-subtle">—</span>}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{s.blocked ? <Tip content={t("Gesperrte Ware, z. B. beschädigt oder in Klärung")}><span className="text-warning">{fmtNumber(s.blocked)}</span></Tip> : <span className="text-fg-subtle">—</span>}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{s.announced ? <Link to="/inbounds" className="text-info hover:underline">+{fmtNumber(s.announced)}</Link> : <span className="text-fg-subtle">—</span>}</td>
      <td className="px-3 py-2.5 text-right"><BufferCell s={s} /></td>
      <td className="pl-3 pr-4 py-2.5">
        {s.inSyncWithShop === null ? (
          <Badge>{t("noch nicht gemeldet")}</Badge>
        ) : s.inSyncWithShop ? (
          <span className="inline-flex items-center gap-1 text-[12px] text-success"><CheckCircle2 className="size-3.5" /> {t("aktuell")}</span>
        ) : (
          <Badge tone="warning">{t("wird abgeglichen")}</Badge>
        )}
      </td>
    </tr>
  );
}

function SortTh({ label, active, onClick, className }: { label: string; active: boolean; onClick: () => void; className?: string }) {
  return (
    <th className={cx("h-9 px-3 font-medium", className)}>
      <button onClick={onClick} className={cx("inline-flex items-center gap-1 hover:text-fg", active && "text-fg")}>
        {t(label)} <ArrowUpDown className="size-3" />
      </button>
    </th>
  );
}

function Kpi({ icon, label, value, sub, tone, onClick, active }: { icon: React.ReactNode; label: string; value: string | null; sub: string; tone?: "danger" | "warning" | "success"; onClick?: () => void; active?: boolean }) {
  const Comp = onClick ? "button" : "div";
  return (
    // Kachel = Filter: aktiv hervorgehoben, erneuter Klick hebt den Filter auf
    <Comp onClick={onClick} aria-pressed={onClick ? !!active : undefined} title={onClick ? (active ? t("Filter aufheben") : t("Als Filter anwenden")) : undefined} className={cx("text-left rounded-xl border bg-surface p-4 transition-shadow", active ? "border-ink ring-1 ring-ink" : "border-border", onClick && !active && "hover:shadow-md hover:border-border-strong")}>
      <div className="flex items-center gap-2 text-fg-muted text-[12.5px]"><span className="text-fg-subtle">{icon}</span>{t(label)}</div>
      <div className="mt-2 text-[26px] font-semibold tracking-tight tabular-nums">{value ?? <Skeleton className="h-8 w-16" />}</div>
      <div className={cx("text-[12px]", tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : tone === "success" ? "text-success" : "text-fg-subtle")}>{t(sub)}</div>
    </Comp>
  );
}
